<template>
  <div>
    <md-field :class="{ 'md-has-value': true, 'flex': true }">
      <label
        v-if='readOnly === false'>
        {{ fieldTitle }}
      </label>
      <div
        class="value"
        @click="showDialog">
        <span>
          <md-icon
            class="action-icon"
            v-if='readOnly === false'>loop</md-icon>
          <md-icon
            class="action-icon"
            v-else>remove_red_eye</md-icon>
          <div class="values-summary">{{ valuesSummary }}<template v-if='readOnly === false'>{{ unit }} / an</template></div>
        </span>
        <md-tooltip
          md-direction="top"
          v-if='readOnly === false'>Définir les valeurs</md-tooltip>
        <md-tooltip
          md-direction="top"
          v-else>Consulter les valeurs</md-tooltip>
      </div>
    </md-field>
    <md-dialog-prompt
      v-if='readOnly === false'
      :md-active.sync="isPromptOpen"
      @md-confirm="hidePrompt"
      @md-cancel="hidePrompt"
      v-model.number="forceValue"
      md-title="Définir une valeur pour toutes les années"
      :md-input-placeholder="promptPlaceholder"
      md-confirm-text="Valider"
      md-cancel-text="Annuler" />
    <md-dialog
      class="dialog"
      @md-clicked-outside="cancel"
      @keyup.esc="cancel"
      :md-active.sync="isDialogOpen">
      <md-dialog-title>{{ title }} <template v-if="showCounter">({{ counterTitle }}: {{ counterTotal }})</template></md-dialog-title>
      <md-dialog-content>
        <form
          novalidate
          @submit.prevent="submit">
          <div class="md-layout md-gutter">
            <div
              v-for="(yearsCluser, index) in yearsCluserList"
              :key="`yearcluster-${index}`"
              class="md-layout-item md-xsmall-size-100 md-small-size-50 md-medium-size-33">
              <div class="year__cluster"><span>{{ yearsCluser[0] }}</span> à <span>{{ yearsCluser[1] }}</span></div>
              <template
                v-for="(yearValue, j) in valuesList">
                <md-field
                  v-if="yearValue.year >= yearsCluser[0] && yearValue.year <= yearsCluser[1]"
                  :key="yearValue.year"
                  :class="{ 'md-invalid': $v.valuesList[j].$error, 'small-field': true }">
                  <label for="yearValue">{{ yearValue.year }}</label>
                  <md-input
                    class="small-input"
                    type="number"
                    step="any"
                    :min="minValue"
                    :disabled="readOnly"
                    required
                    id="yearValue"
                    v-model.number="yearValue.value"
                    @input="$v.valuesList[j].value.$touch()" />
                  <span
                    class="md-error"
                    v-if="!$v.valuesList[j].value.required">Ce champ est obligatoire</span>
                  <span
                    class="md-error"
                    v-if="!$v.valuesList[j].value.minValue">Entrez une valeur positive</span>
                  <span class="md-suffix">{{ unit }}</span>
                </md-field>
              </template>
            </div>
          </div>
        </form>
      </md-dialog-content>
      <md-dialog-actions class="dialog-actions">
        <div
          class="md-layout"
          v-if='readOnly === false'>
          <div
            class="md-layout-item dialog-action-left">
            <md-button
              class="md-primary"
              @click="showPrompt">
              Définir une valeur pour toutes les années
            </md-button>
          </div>
          <div class="md-layout-item dialog-action-right">
            <md-button
              @click="cancel()"
              class="md-accent">Annuler</md-button>
            <md-button
              @click="validate()"
              class="md-primary md-raised">Valider</md-button>
          </div>
        </div>
        <div
          class="md-layout"
          v-else>
          <div class="md-layout-item dialog-action-right">
            <md-button
              @click="cancel()"
              class="md-primary md-raised">Fermer</md-button>
          </div>
        </div>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, minValue } from 'vuelidate/lib/validators'
import cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'YearlyValuesField',
  mixins: [validationMixin],
  data () {
    return {
      private: {
        forceValue: null
      },
      forceValueRequired: false,
      isDialogOpen: false,
      isPromptOpen: false,
      valuesList: [],
      previousValuesList: []
    }
  },
  props: {
    yearsCluserList: {
      type: Array,
      required: true
    },
    initialValuesList: {
      type: Array,
      required: true
    },
    unit: {
      type: String,
      required: true
    },
    fieldTitle: {
      type: String,
      default: undefined
    },
    dialogTitle: {
      type: String,
      default: undefined
    },
    showCounter: {
      type: Boolean,
      required: false,
      default: () => false
    },
    counterTitle: {
      type: String,
      required: false,
      default: () => ''
    },
    minYear: {
      type: Number,
      required: true
    },
    maxYear: {
      type: Number,
      required: true
    },
    allowNegativeValue: {
      type: Boolean,
      required: false,
      default: false
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  watch: {
    initialValuesList: function (newValues, oldValues) {
      /* We need to sync manually valuesList when initialValuesList change */
      this.valuesList = cloneDeep(newValues)
    }
  },
  mounted: function () {
    var app = this
    app.valuesList = cloneDeep(app.initialValuesList)
  },
  methods: {
    resetPreviousValuesList: function () {
      this.valuesList = cloneDeep(this.previousValuesList)
    },
    cancel: function () {
      this.resetPreviousValuesList()
      this.hideDialog()
    },
    validate: function () {
      var app = this
      app.$emit('validate:valuesList', app.valuesList)
      app.hideDialog()
    },
    applyForceValue: function () {
      var app = this
      if (app.private.forceValue !== null) {
        if (app.private.forceValue >= 0 || this.allowNegativeValue) {
          for (var i in app.valuesList) {
            app.valuesList[i].value = app.private.forceValue
          }
        } else {
          app.forceValueRequired = true
          app.$v.forceValue.$touch()
        }
      }
      app.isPromptOpen = false
    },
    showDialog: function () {
      var app = this
      app.isDialogOpen = true
      this.previousValuesList = cloneDeep(this.valuesList)
    },
    hideDialog: function () {
      var app = this
      app.isDialogOpen = false
    },
    showPrompt: function () {
      var app = this
      app.hideDialog()
      app.isPromptOpen = true
    },
    hidePrompt: function () {
      var app = this
      app.isPromptOpen = false
      app.isDialogOpen = true // we don't call showDialog because we don't want to update previousValuesList
    }
  },
  computed: {
    minValue () {
      let minValue = 0
      if (this.allowNegativeValue) {
        minValue = -100
      }
      return minValue
    },
    forceValue: {
      get () {
        return this.private.forceValue
      },
      set (newValue) {
        this.private.forceValue = newValue
        this.applyForceValue()
      }
    },
    promptPlaceholder () {
      var app = this
      return 'valeur en ' + app.unit
    },
    valuesSummary () {
      var app = this
      var min = null
      var max = null

      for (var i in app.valuesList) {
        if (app.valuesList[i].year >= app.minYear && app.valuesList[i].year <= app.maxYear && app.valuesList[i]['value'] !== null) {
          if (min === null || min > app.valuesList[i]['value']) {
            min = app.valuesList[i]['value']
          }

          if (max === null || max < app.valuesList[i]['value']) {
            max = app.valuesList[i]['value']
          }
        }
      }
      if (min === max) {
        return min
      } else if (min < max) {
        return min + ' à ' + max
      } else {
        return 'Indéfini'
      }
    },
    title () {
      var app = this

      if (app.dialogTitle === undefined && app.fieldTitle !== undefined) {
        return app.fieldTitle
      } else if (app.dialogTitle !== undefined) {
        return app.dialogTitle
      } else {
        return ''
      }
    },
    counterTotal () {
      return this.valuesList.reduce((sum, yearValue) => {
        return sum + yearValue.value
      }, 0)
    }

  },
  validations () {
    var validations = {
      forceValue: {},
      valuesList: {}
    }

    for (var j in this.valuesList) {
      if (!this.allowNegativeValue) {
        validations.valuesList[j] = {
          value: {
            required,
            minValue: minValue(0)
          }
        }
      } else {
        validations.valuesList[j] = {
          value: {
            required
          }
        }
      }
    }

    if (this.forceValueRequired && !this.allowNegativeValue) {
      validations.forceValue = {
        minValue: minValue(0),
        required
      }
    } else if (!this.allowNegativeValue) {
      validations.forceValue = {
        minValue: minValue(0)
      }
    }

    return validations
  }
}
</script>

<style lang="scss" scoped>
.values-summary{
  min-width: 40px;
}

.dialog {
  min-width: 80vw;
}
.action-icon {
  float: right;
}
.small-input {
  text-align: right;
  max-width: 87%;
}
.button-full-width {
  width: 100%;
}
.flex {
  display: flex;
  align-items: flex-end;
  line-height: 24px;

  div {
    margin-right: 30px;
    &.label {
      min-width: 300px;
    }
    &.value {
      cursor: pointer;
    }
    span {
      padding-bottom: 5px;
    }
  }
}
.year__cluster {
  background: #eee;
  padding: 10px;
  text-align: center;
  font-weight: bold;
  span {
    font-size: 16px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/YearlyValuesField.vue